import React, { useEffect, useState } from 'react';
import { Descriptions, Spin, Table, Timeline, Typography, Empty } from 'antd';
import axiosInstance from '../axios/axiosInstance';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

function formatDate(value, dash) {
    if (!value) return dash;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY HH:mm') : value;
}

function formatDateOnly(value, dash) {
    if (!value) return dash;
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format('DD.MM.YYYY') : value;
}

export default function PatientHistoryPanel({ patientId }) {
    const { t } = useTranslation();
    const dash = t('common.dash');
    const [history, setHistory] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!patientId) return;

        let cancelled = false;

        const load = async () => {
            setLoading(true);
            try {
                const response = await axiosInstance.get(`/patients/${patientId}/history`);
                if (!cancelled) {
                    setHistory(response.data.data ?? response.data);
                }
            } catch (error) {
                console.error('Patient history fetch failed:', error);
                if (!cancelled) setHistory(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => {
            cancelled = true;
        };
    }, [patientId]);

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <Spin size="large" />
            </div>
        );
    }

    if (!history) {
        return <Empty description={t('history.loadFailed')} />;
    }

    const nextVisit = history.next_visit;
    const nextContact = history.next_contact;

    const visitColumns = [
        {
            title: t('common.date'),
            dataIndex: 'date_at',
            key: 'date_at',
            render: (_, record) => formatDate(record.date_at ?? record.created_at ?? record.start_at, dash),
        },
        {
            title: t('common.doctor'),
            key: 'doctor',
            render: (_, record) => record.doctor?.name ?? record.doctor_name ?? dash,
        },
        {
            title: t('history.services'),
            key: 'services',
            render: (_, record) => {
                const orders = record.orders ?? record.services ?? [];
                if (!Array.isArray(orders) || orders.length === 0) return dash;
                return orders
                    .map((o) => o.service?.name ?? o.name ?? o.service_name)
                    .filter(Boolean)
                    .join(', ');
            },
        },
        {
            title: t('history.registeredBy'),
            key: 'registered_by',
            render: (_, record) =>
                record.registered_by?.name ??
                record.created_by?.name ??
                record.user?.name ??
                dash,
        },
    ];

    const visits = history.visits ?? [];

    return (
        <div className="space-y-6">
            <Descriptions bordered column={1} title={t('history.summary')}>
                <Descriptions.Item label={t('history.lastVisit')}>
                    {formatDateOnly(history.last_visited_at, dash)}
                </Descriptions.Item>
                <Descriptions.Item label={t('history.nextVisit')}>
                    {nextVisit ? (
                        <span>
                            {formatDateOnly(nextVisit.start_at ?? nextVisit.date ?? nextVisit.start_at_date, dash)}
                            {nextVisit.start_time ? ` ${nextVisit.start_time}` : ` — ${t('history.timeNotSet')}`}
                        </span>
                    ) : (
                        dash
                    )}
                </Descriptions.Item>
                <Descriptions.Item label={t('history.nextContact')}>
                    {nextVisit
                        ? dash
                        : nextContact
                          ? formatDateOnly(nextContact.due_at ?? nextContact.contact_at ?? nextContact.date, dash)
                          : dash}
                </Descriptions.Item>
            </Descriptions>

            <div>
                <Typography.Title level={5}>{t('history.visitsHistory')}</Typography.Title>
                <Table
                    rowKey={(row) => row.id ?? row.visit_id ?? JSON.stringify(row)}
                    dataSource={visits}
                    columns={visitColumns}
                    pagination={visits.length > 10 ? { pageSize: 10 } : false}
                    locale={{ emptyText: t('history.noVisits') }}
                />
            </div>

            <div>
                <Typography.Title level={5}>{t('history.callsHistory')}</Typography.Title>
                {(history.follow_up_history ?? []).length === 0 ? (
                    <Empty description={t('history.noCalls')} />
                ) : (
                    <Timeline
                        items={(history.follow_up_history ?? []).map((item, index) => {
                            const statusKey = {
                                no_answer: 'history.noAnswer',
                                contacted: 'history.contacted',
                                will_visit: 'history.willVisit',
                            }[item.status];
                            return {
                            key: item.id ?? index,
                            children: (
                                <div>
                                    <div className="font-medium">
                                        {statusKey ? t(statusKey) : item.status ?? dash}
                                        {item.created_at ? ` · ${formatDate(item.created_at, dash)}` : ''}
                                    </div>
                                    {item.description && (
                                        <div className="text-gray-600 text-sm mt-1">{item.description}</div>
                                    )}
                                    {item.user?.name && (
                                        <div className="text-gray-500 text-xs mt-1">{item.user.name}</div>
                                    )}
                                </div>
                            ),
                        };
                        })}
                    />
                )}
            </div>
        </div>
    );
}
